import type { BotRuntime } from "./bot-runtime";
import type { GameFlow, GameFlowSnapshot } from "./game-flow";
import type { PlayerRuntime } from "./player-runtime";
import { gameplayConfig } from "../config/gameplay-config";
import { recordBoundedIdentifier } from "./runtime-stability";
import {
  areCombatantsContacting,
  resolveSizeCombatContact,
  type AcceptedCombatResolution,
  type SizeCombatant,
} from "../simulation/size-combat";

export interface CombatRuntimeSnapshot {
  readonly lastResolution: AcceptedCombatResolution | null;
}

export interface CombatRuntimeStabilitySnapshot {
  readonly activePairCount: number;
  readonly processedContactCount: number;
}

export type CombatResolutionListener = (
  resolution: AcceptedCombatResolution,
) => void;

const cloneResolution = (
  resolution: AcceptedCombatResolution,
): AcceptedCombatResolution =>
  resolution.kind === "defeat"
    ? { ...resolution }
    : {
        ...resolution,
        first: {
          ...resolution.first,
          position: { ...resolution.first.position },
        },
        second: {
          ...resolution.second,
          position: { ...resolution.second.position },
        },
      };

const pairId = (firstId: string, secondId: string): string =>
  `${firstId}:${secondId}`;

export class CombatRuntime {
  private activePairIds = new Set<string>();
  private contactSequence = 0;
  private lastResolution: AcceptedCombatResolution | null = null;
  private readonly listeners = new Set<CombatResolutionListener>();
  private readonly processedContactIds = new Set<string>();
  private runId = 0;

  public constructor(
    flow: GameFlow,
    private readonly playerRuntime: Pick<
      PlayerRuntime,
      "applyBodyCollision" | "getSnapshot" | "subscribeFixedStep"
    >,
    private readonly botRuntime: Pick<
      BotRuntime,
      "applyBodyCollision" | "getSnapshot"
    >,
  ) {
    playerRuntime.subscribeFixedStep((_stepMilliseconds, simulationTimeMs) => {
      this.step(simulationTimeMs);
    });
    flow.subscribe((snapshot) => {
      this.applyFlow(snapshot);
    });
  }

  public getSnapshot(): CombatRuntimeSnapshot {
    return {
      lastResolution:
        this.lastResolution === null
          ? null
          : cloneResolution(this.lastResolution),
    };
  }

  public getStabilitySnapshot(): CombatRuntimeStabilitySnapshot {
    return {
      activePairCount: this.activePairIds.size,
      processedContactCount: this.processedContactIds.size,
    };
  }

  public subscribe(listener: CombatResolutionListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private applyFlow(snapshot: GameFlowSnapshot): void {
    if (snapshot.scene === "results") {
      return;
    }
    this.activePairIds.clear();
    this.processedContactIds.clear();
    this.contactSequence = 0;
    this.lastResolution = null;
    this.runId =
      snapshot.scene === "gameplay"
        ? this.playerRuntime.getSnapshot().runId
        : 0;
  }

  private createCombatants(): SizeCombatant[] {
    const player = this.playerRuntime.getSnapshot().player;
    const bots = this.botRuntime.getSnapshot().bots;
    const combatants: SizeCombatant[] = bots.map((bot) => ({
      directionRadians: bot.directionRadians,
      id: bot.id,
      kind: "bot",
      level: bot.traits.level,
      position: { ...bot.position },
      radius: bot.collision.radius,
    }));
    if (player?.alive === true) {
      combatants.push({
        directionRadians: player.directionRadians,
        id: "player",
        kind: "player",
        level: player.level,
        position: { ...player.position },
        radius: player.collision.radius,
      });
    }
    return combatants.sort((first, second) =>
      first.id.localeCompare(second.id),
    );
  }

  private step(simulationTimeMs: number): void {
    if (this.runId === 0) {
      return;
    }
    const combatants = this.createCombatants();
    const currentPairIds = new Set<string>();
    const defeatedIds = new Set<string>();
    for (const [firstIndex, first] of combatants.entries()) {
      for (const second of combatants.slice(firstIndex + 1)) {
        if (defeatedIds.has(first.id) || defeatedIds.has(second.id)) {
          continue;
        }
        if (!areCombatantsContacting(first, second, gameplayConfig.combat)) {
          continue;
        }
        const identifier = pairId(first.id, second.id);
        currentPairIds.add(identifier);
        if (this.activePairIds.has(identifier)) {
          continue;
        }
        this.contactSequence += 1;
        const contactId = `${String(this.runId)}:contact-${String(this.contactSequence).padStart(4, "0")}`;
        const resolution = resolveSizeCombatContact(
          {
            activeRunId: this.runId,
            combatants,
            contact: {
              firstId: first.id,
              id: contactId,
              observedAtMs: simulationTimeMs,
              runId: this.runId,
              secondId: second.id,
            },
            currentSimulationTimeMs: simulationTimeMs,
            processedContactIds: this.processedContactIds,
          },
          gameplayConfig.combat,
        );
        if (resolution.kind === "ignored") {
          continue;
        }
        recordBoundedIdentifier(
          this.processedContactIds,
          contactId,
          gameplayConfig.runtimeStability.maximumProcessedEvents,
        );
        this.lastResolution = resolution;
        if (resolution.kind === "defeat") {
          defeatedIds.add(resolution.defeatedId);
        }
        if (resolution.kind === "separate") {
          this.applySeparation(
            resolution.first.id,
            resolution.first.position,
            resolution.first.directionRadians,
          );
          this.applySeparation(
            resolution.second.id,
            resolution.second.position,
            resolution.second.directionRadians,
          );
        }
        this.notify(resolution);
      }
    }
    this.activePairIds = currentPairIds;
  }

  private notify(resolution: AcceptedCombatResolution): void {
    for (const listener of this.listeners) {
      listener(cloneResolution(resolution));
    }
  }

  private applySeparation(
    identifier: string,
    position: { readonly x: number; readonly y: number },
    directionRadians: number,
  ): void {
    if (identifier === "player") {
      this.playerRuntime.applyBodyCollision(position, directionRadians);
      return;
    }
    this.botRuntime.applyBodyCollision(identifier, position, directionRadians);
  }
}
